import { epicengageSupabasev1 } from "../integrations/supabase/client";

export type AudiencePreset = "all" | "new" | "active" | "inactive";

export type AudienceFilter = {
  preset: AudiencePreset;
  status?: string | "all";
  tags: string[];
  /** When non-empty, audience is restricted to these specific customer ids (overrides preset/tags/status). */
  customer_ids?: string[];
};

export const defaultAudienceFilter: AudienceFilter = {
  preset: "all",
  status: "all",
  tags: [],
  customer_ids: [],
};

const TAG_PAGE_SIZE = 1000;
const ID_CHUNK_SIZE = 500;

function applyAudienceFilters(query: any, f: AudienceFilter) {
  let q = query.eq("archived", false).is("unsubscribed_at", null);

  if (f.status && f.status !== "all") q = q.eq("status", f.status);
  if (f.preset === "new") {
    const cutoff = new Date(Date.now() - 30 * 86400000).toISOString();
    q = q.gte("joined_at", cutoff);
  } else if (f.preset === "active") {
    q = q.eq("status", "active");
  } else if (f.preset === "inactive") {
    q = q.eq("status", "inactive");
  }

  return q;
}

async function fetchTaggedCustomerIds(merchantId: string, tags: string[]) {
  const byCust = new Map<string, Set<string>>();
  let offset = 0;

  while (true) {
    const { data } = await epicengageSupabasev1
      .from("customer_tags")
      .select("customer_id, tag")
      .eq("merchant_id", merchantId)
      .in("tag", tags)
      .range(offset, offset + TAG_PAGE_SIZE - 1);

    const rows = data || [];
    rows.forEach((r) => {
      if (!byCust.has(r.customer_id)) byCust.set(r.customer_id, new Set());
      byCust.get(r.customer_id)!.add(r.tag);
    });

    if (rows.length < TAG_PAGE_SIZE) break;
    offset += TAG_PAGE_SIZE;
  }

  return Array.from(byCust.entries())
    .filter(([, set]) => tags.every((t) => set.has(t)))
    .map(([id]) => id);
}

export async function countAudience(merchantId: string, f: AudienceFilter): Promise<number> {
  if (f.customer_ids && f.customer_ids.length > 0) {
    let total = 0;
    for (let i = 0; i < f.customer_ids.length; i += ID_CHUNK_SIZE) {
      const chunk = f.customer_ids.slice(i, i + ID_CHUNK_SIZE);
      const { count } = await epicengageSupabasev1
        .from("customers")
        .select("id", { count: "exact", head: true })
        .eq("merchant_id", merchantId)
        .eq("archived", false)
        .is("unsubscribed_at", null)
        .in("id", chunk);
      total += count ?? 0;
    }
    return total;
  }

  const baseQuery = () => applyAudienceFilters(
    epicengageSupabasev1
      .from("customers")
      .select("id", { count: "exact", head: f.tags.length === 0 })
      .eq("merchant_id", merchantId),
    f,
  );

  if (f.tags.length === 0) {
    const { count } = await baseQuery();
    return count ?? 0;
  }

  const taggedIds = await fetchTaggedCustomerIds(merchantId, f.tags);
  if (taggedIds.length === 0) return 0;

  let total = 0;
  for (let i = 0; i < taggedIds.length; i += ID_CHUNK_SIZE) {
    const chunk = taggedIds.slice(i, i + ID_CHUNK_SIZE);
    const { count } = await applyAudienceFilters(
      epicengageSupabasev1
        .from("customers")
        .select("id", { count: "exact", head: true })
        .eq("merchant_id", merchantId)
        .in("id", chunk),
      f,
    );
    total += count ?? 0;
  }

  return total;
}

export function describeAudience(f: AudienceFilter): string {
  if (f.customer_ids && f.customer_ids.length > 0) {
    const n = f.customer_ids.length;
    return `${n} hand-picked customer${n === 1 ? "" : "s"}`;
  }
  const parts: string[] = [];
  if (f.preset === "new") parts.push("New customers (last 30d)");
  else if (f.preset === "active") parts.push("Active customers");
  else if (f.preset === "inactive") parts.push("Inactive customers");
  else parts.push("All customers");
  if (f.status && f.status !== "all" && f.preset === "all") parts.push(`status: ${f.status}`);
  if (f.tags.length > 0) parts.push(`tags: ${f.tags.join(", ")}`);
  return parts.join(" · ");
}
